import { createContext, useContext, useState, useEffect } from "react";

// Supported languages
export const LANGUAGES = [
  { code: "en", name: "English", flag: "🇺🇸" },
  { code: "es", name: "Español", flag: "🇪🇸" },
  { code: "zh", name: "中文", flag: "🇨🇳" },
  { code: "vi", name: "Tiếng Việt", flag: "🇻🇳" },
  { code: "ko", name: "한국어", flag: "🇰🇷" },
  { code: "ru", name: "Русский", flag: "🇷🇺" }
];

// Translations
const translations = {
  en: {
    // Common
    welcome: "Welcome",
    loading: "Loading...",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    search: "Search",
    filter: "Filter",
    export: "Export",
    
    // Navigation
    dashboard: "Dashboard",
    requests: "Requests",
    schedule: "Schedule",
    drivers: "Drivers",
    clients: "Clients",
    liveMap: "Live Map",
    claims: "Claims",
    settings: "Settings",
    signOut: "Sign Out",
    
    // Rides
    newRide: "New Ride",
    requestRide: "Request Ride",
    pickupAddress: "Pickup Address",
    dropoffAddress: "Dropoff Address",
    pickupTime: "Pickup Time",
    pickupDate: "Pickup Date",
    vehicleType: "Vehicle Type",
    specialRequests: "Special Requests",
    
    // Status
    pending: "Pending",
    approved: "Approved",
    scheduled: "Scheduled",
    inProgress: "In Progress",
    arrived: "Arrived",
    completed: "Completed",
    cancelled: "Cancelled",
    
    // Client
    trackYourRide: "Track Your Ride",
    driverOnTheWay: "Driver on the way",
    driverArrived: "Driver has arrived",
    estimatedArrival: "Estimated Arrival",
    shareETA: "Share ETA",
    callDriver: "Call Driver",
    
    // Driver
    startRide: "Start Ride",
    completeRide: "Complete Ride",
    navigate: "Navigate",
    clientInfo: "Client Info",
    accessibilityNeeds: "Accessibility Needs",
    
    // Dispatch
    assignDriver: "Assign Driver",
    approve: "Approve",
    decline: "Decline",
    fleetStatus: "Fleet Status",
    available: "Available",
    onRide: "On Ride",
    
    // Insurance
    insuranceClaims: "Insurance Claims",
    createClaim: "Create Claim",
    claimType: "Claim Type",
    memberId: "Member ID",
    medicaid: "Medicaid",
    medicare: "Medicare",
    privateInsurance: "Private Insurance",
    
    // Recurring
    recurringRides: "Recurring Rides",
    newSchedule: "New Schedule",
    generateRides: "Generate Rides",
    
    // Accessibility
    wheelchair: "Wheelchair",
    walker: "Walker",
    cane: "Cane",
    blind: "Visually Impaired",
    deaf: "Hearing Impaired",
    oxygen: "Oxygen",
    serviceAnimal: "Service Animal"
  },
  
  es: {
    // Common
    welcome: "Bienvenido",
    loading: "Cargando...",
    save: "Guardar",
    cancel: "Cancelar",
    delete: "Eliminar",
    edit: "Editar",
    search: "Buscar",
    filter: "Filtrar",
    export: "Exportar",
    
    // Navigation
    dashboard: "Panel",
    requests: "Solicitudes",
    schedule: "Horario",
    drivers: "Conductores",
    clients: "Clientes",
    liveMap: "Mapa en Vivo",
    claims: "Reclamaciones",
    settings: "Configuración",
    signOut: "Cerrar Sesión",
    
    // Rides
    newRide: "Nuevo Viaje",
    requestRide: "Solicitar Viaje",
    pickupAddress: "Dirección de Recogida",
    dropoffAddress: "Dirección de Destino",
    pickupTime: "Hora de Recogida",
    pickupDate: "Fecha de Recogida",
    vehicleType: "Tipo de Vehículo",
    specialRequests: "Solicitudes Especiales",
    
    // Status
    pending: "Pendiente",
    approved: "Aprobado",
    scheduled: "Programado",
    inProgress: "En Progreso",
    arrived: "Llegó",
    completed: "Completado",
    cancelled: "Cancelado",
    
    // Client
    trackYourRide: "Rastrea Tu Viaje",
    driverOnTheWay: "Conductor en camino",
    driverArrived: "El conductor ha llegado",
    estimatedArrival: "Llegada Estimada",
    shareETA: "Compartir ETA",
    callDriver: "Llamar al Conductor",
    
    // Driver
    startRide: "Iniciar Viaje",
    completeRide: "Completar Viaje",
    navigate: "Navegar",
    clientInfo: "Info del Cliente",
    accessibilityNeeds: "Necesidades de Accesibilidad",
    
    // Dispatch
    assignDriver: "Asignar Conductor",
    approve: "Aprobar",
    decline: "Rechazar",
    fleetStatus: "Estado de la Flota",
    available: "Disponible",
    onRide: "En Viaje",
    
    // Insurance
    insuranceClaims: "Reclamaciones de Seguro",
    createClaim: "Crear Reclamación",
    claimType: "Tipo de Reclamación",
    memberId: "ID de Miembro",
    medicaid: "Medicaid",
    medicare: "Medicare",
    privateInsurance: "Seguro Privado",
    
    // Recurring
    recurringRides: "Viajes Recurrentes",
    newSchedule: "Nuevo Horario",
    generateRides: "Generar Viajes",
    
    // Accessibility
    wheelchair: "Silla de Ruedas",
    walker: "Andador",
    cane: "Bastón",
    blind: "Discapacidad Visual",
    deaf: "Discapacidad Auditiva",
    oxygen: "Oxígeno",
    serviceAnimal: "Animal de Servicio"
  },
  
  zh: {
    // Common
    welcome: "欢迎",
    loading: "加载中...",
    save: "保存",
    cancel: "取消",
    delete: "删除",
    edit: "编辑",
    search: "搜索",
    filter: "筛选",
    export: "导出",
    
    // Navigation
    dashboard: "控制面板",
    requests: "请求",
    schedule: "日程",
    drivers: "司机",
    clients: "客户",
    liveMap: "实时地图",
    claims: "索赔",
    settings: "设置",
    signOut: "退出",
    
    // Rides
    newRide: "新行程",
    requestRide: "请求行程",
    pickupAddress: "接送地址",
    dropoffAddress: "目的地地址",
    pickupTime: "接送时间",
    pickupDate: "接送日期",
    vehicleType: "车辆类型",
    specialRequests: "特殊要求",
    
    // Status
    pending: "待处理",
    approved: "已批准",
    scheduled: "已安排",
    inProgress: "进行中",
    arrived: "已到达",
    completed: "已完成",
    cancelled: "已取消",
    
    // Client
    trackYourRide: "追踪您的行程",
    driverOnTheWay: "司机在路上",
    driverArrived: "司机已到达",
    estimatedArrival: "预计到达",
    shareETA: "分享预计时间",
    callDriver: "呼叫司机",
    
    // Driver
    startRide: "开始行程",
    completeRide: "完成行程",
    navigate: "导航",
    clientInfo: "客户信息",
    accessibilityNeeds: "无障碍需求",
    
    // Dispatch
    assignDriver: "分配司机",
    approve: "批准",
    decline: "拒绝",
    fleetStatus: "车队状态",
    available: "可用",
    onRide: "行程中",
    
    // Insurance
    insuranceClaims: "保险索赔",
    createClaim: "创建索赔",
    claimType: "索赔类型",
    memberId: "会员ID",
    medicaid: "医疗补助",
    medicare: "医疗保险",
    privateInsurance: "私人保险",
    
    // Recurring
    recurringRides: "定期行程",
    newSchedule: "新日程",
    generateRides: "生成行程",
    
    // Accessibility
    wheelchair: "轮椅",
    walker: "助行器",
    cane: "手杖",
    blind: "视力障碍",
    deaf: "听力障碍",
    oxygen: "氧气",
    serviceAnimal: "服务动物"
  }
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    // Try to get from localStorage or browser preference
    const saved = localStorage.getItem("preferred_language");
    if (saved && LANGUAGES.find(l => l.code === saved)) {
      return saved;
    }
    // Try browser language
    const browserLang = navigator.language.split("-")[0];
    if (LANGUAGES.find(l => l.code === browserLang)) {
      return browserLang;
    }
    return "en";
  });

  useEffect(() => {
    localStorage.setItem("preferred_language", language);
  }, [language]);

  // Translation function
  const t = (key) => {
    const langTranslations = translations[language] || translations.en;
    return langTranslations[key] || translations.en[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}

// Language selector component
export function LanguageSelector({ className = "" }) {
  const { language, setLanguage, LANGUAGES } = useLanguage();
  const currentLang = LANGUAGES.find(l => l.code === language);

  return (
    <div className={`relative ${className}`}>
      <select
        value={language}
        onChange={(e) => setLanguage(e.target.value)}
        className="appearance-none bg-transparent border border-slate-300 rounded-lg px-3 py-2 pr-8 text-sm cursor-pointer hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
      >
        {LANGUAGES.map(lang => (
          <option key={lang.code} value={lang.code}>
            {lang.flag} {lang.name}
          </option>
        ))}
      </select>
      <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  );
}
